<template>
  <div class="lg:hidden">
    <div ref="toolbarRef" class="mb-3 grid grid-cols-2 gap-3">
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 rounded-lg border border-default bg-default px-3 py-3 text-sm font-medium text-highlighted shadow-sm transition-colors hover:bg-muted"
        @click="sortOpen = true"
      >
        <UIcon name="i-heroicons-arrows-up-down" class="size-5 shrink-0 text-muted" />
        <span>Ordonează</span>
      </button>

      <button
        type="button"
        class="relative inline-flex items-center justify-center gap-2 rounded-lg border border-default bg-default px-3 py-3 text-sm font-medium text-highlighted shadow-sm transition-colors hover:bg-muted"
        @click="filtersOpen = true"
      >
        <UIcon name="i-heroicons-adjustments-horizontal" class="size-5 shrink-0 text-muted" />
        <span>Filtrează</span>
        <span
          v-if="activeFilterCount > 0"
          class="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-gray-900 text-[11px] font-semibold text-white"
        >
          {{ activeFilterCount }}
        </span>
      </button>
    </div>

    <Teleport to="body">
      <Transition name="toolbar-slide">
        <div
          v-if="showStickyBar"
          class="fixed inset-x-0 bottom-0 z-40 border-t border-default bg-default/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden"
        >
          <div class="mx-auto grid max-w-md grid-cols-2 gap-3">
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-lg border border-default bg-default px-3 py-3 text-sm font-medium text-highlighted shadow-sm transition-colors hover:bg-muted"
              @click="sortOpen = true"
            >
              <UIcon name="i-heroicons-arrows-up-down" class="size-5 shrink-0 text-muted" />
              <span>Ordonează</span>
            </button>

            <button
              type="button"
              class="relative inline-flex items-center justify-center gap-2 rounded-lg border border-default bg-default px-3 py-3 text-sm font-medium text-highlighted shadow-sm transition-colors hover:bg-muted"
              @click="filtersOpen = true"
            >
              <UIcon name="i-heroicons-adjustments-horizontal" class="size-5 shrink-0 text-muted" />
              <span>Filtrează</span>
              <span
                v-if="activeFilterCount > 0"
                class="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-gray-900 text-[11px] font-semibold text-white"
              >
                {{ activeFilterCount }}
              </span>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <USlideover v-model:open="sortOpen" title="Ordonează" side="bottom">
      <template #body>
        <ProductSort
          :filters="filters"
          @update="onSortUpdate"
        />
      </template>
    </USlideover>

    <USlideover v-model:open="filtersOpen" title="Filtrează" side="right">
      <template #body>
        <ProductFilters
          ref="productFiltersRef"
          bare
          hide-sort
          :filters="filters"
          @update="onFiltersUpdate"
        />
      </template>
      <template #footer>
        <div class="grid w-full grid-cols-2 gap-3">
          <UButton
            label="Resetează filtrele"
            variant="solid"
            color="neutral"
            size="lg"
            block
            @click="onResetFilters"
          />
          <UButton
            label="Aplică filtre"
            color="primary"
            size="lg"
            block
            @click="filtersOpen = false"
          />
        </div>
      </template>
    </USlideover>
  </div>
</template>

<script setup lang="ts">
import type { Brand, ProductAttribute, ProductFilterState } from '~/types'
import { countActiveProductFilters } from '~/utils/productFilters'

const props = defineProps<{
  filters: ProductFilterState
  brands: Brand[]
  attributes: ProductAttribute[]
}>()

const emit = defineEmits<{
  update: [filters: ProductFilterState]
}>()

const sortOpen = ref(false)
const filtersOpen = ref(false)
const productFiltersRef = ref<{ reset: () => void } | null>(null)
const toolbarRef = ref<HTMLElement | null>(null)
const showStickyBar = ref(false)

useIntersectionObserver(toolbarRef, ([entry]) => {
  // Show the sticky bar only when the inline toolbar has scrolled above the viewport.
  showStickyBar.value = !entry.isIntersecting && entry.boundingClientRect.top < 0
})

const activeFilterCount = computed(() => countActiveProductFilters(props.filters))

function onSortUpdate(newFilters: ProductFilterState) {
  emit('update', newFilters)
  sortOpen.value = false
}

function onFiltersUpdate(newFilters: ProductFilterState) {
  emit('update', newFilters)
}

function onResetFilters() {
  productFiltersRef.value?.reset()
}
</script>
